import { useCallback, useState } from 'react';
import type { ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { insightFetch, type InsightSection } from './api';

/**
 * Force one or more SectionInsight cards to recompute right now, from a mutation's own success
 * handler (a financials upload landed, a Registry Intelligence pull finished, peers were
 * refreshed, a business storyboard was rewritten) — so the summary visibly reflects the new data
 * immediately, instead of silently showing a pre-change read until the next natural refetch.
 * Deliberately NOT wired to tab navigation or the deal-load query — opening a tab whose underlying
 * data hasn't changed still just serves the existing (still-valid) cached summary, no extra LLM
 * spend; this only fires from an actual data-changing action. Fire-and-forget: never throws, so a
 * summary-refresh failure can't break the calling action.
 */
export function useInsightRefresh(dealId: string) {
  const qc = useQueryClient();
  return useCallback((sections: InsightSection[]) => {
    for (const section of sections) {
      insightFetch(dealId, section, true)
        .then((res) => qc.setQueryData(['section-insight', dealId, section], res))
        .catch(() => {});
    }
  }, [dealId, qc]);
}

/**
 * Shared "make Probe42's raw JSON readable" formatting — used by the Registry Intelligence tab and
 * by the supplementary Probe42-sourced cards on Overview / Financials / Peers & Industry / Compliance
 * (decision.md D17/D18). Nothing here should ever render `{"key":"value"}` to a viewer — a nested
 * array-of-records becomes its own small nested table, a nested flat object becomes a capped
 * "Key: value" line, dates/amounts/percentages/booleans are formatted, everything else prints as-is.
 * The one place raw JSON is still shown is an explicit, opt-in "View raw JSON" expander.
 */

export const humanize = (k: string): string => k.replace(/[_-]+/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());

/** An array where every item is itself a record (not a primitive, not a nested array) — the common
 *  shape for Probe42's yearly/per-filing data (financials, credit ratings, GST filings, legal cases,
 *  name history, …) — worth a real table instead of a JSON dump. */
export function isArrayOfRecords(v: unknown): v is Record<string, unknown>[] {
  return Array.isArray(v) && v.length > 0 && v.every((x) => x !== null && typeof x === 'object' && !Array.isArray(x));
}

// Value formatting heuristics run off the field NAME (Probe42 doesn't type its fields) — deliberately
// conservative, worst case a value falls back to its plain form, never garbled.
const DATE_KEY_RE = /(_date|^date_|_at)$/i;
const AMOUNT_KEY_RE = /(amount|capital|assets|liabilit|revenue|turnover|income|expense|dues|proceeds|consideration|charges_amount)/i;
const PCT_KEY_RE = /(percentage|_pct$|growth$|margin$)/i;

function fmtDate(v: string): string {
  const d = new Date(v);
  return isNaN(d.getTime()) ? v : d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

/** Formats a single scalar value for display — always a plain string (composable in table cells). */
export function fmtP42Value(key: string, v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (typeof v === 'string' && DATE_KEY_RE.test(key) && /^\d{4}-\d{2}-\d{2}/.test(v)) return fmtDate(v);
  if (typeof v === 'number') {
    if (AMOUNT_KEY_RE.test(key) && !PCT_KEY_RE.test(key) && Math.abs(v) >= 100000) return `₹${(v / 1e7).toLocaleString('en-IN', { maximumFractionDigits: 2 })} Cr`;
    if (PCT_KEY_RE.test(key)) return `${v.toFixed(2)}%`;
    return v.toLocaleString('en-IN');
  }
  return String(v);
}

const INLINE_FIELD_CAP = 6;
/** Renders one table CELL's value — never raw JSON. A nested array-of-records becomes a small nested
 *  table; a nested flat object becomes a short "Key: value · Key: value" line (capped); everything
 *  else is a formatted scalar. */
export function P42CellContent({ k, v }: { k: string; v: unknown }): ReactNode {
  if (v === null || v === undefined || v === '') return <span className="text-muted-2">—</span>;
  if (Array.isArray(v)) {
    if (!v.length) return <span className="text-muted-2">—</span>;
    if (isArrayOfRecords(v)) return <div className="my-1"><P42Table rows={v} /></div>;
    return <>{v.map((x) => fmtP42Value(k, x)).join(', ')}</>;
  }
  if (typeof v === 'object') {
    const entries = Object.entries(v as Record<string, unknown>).filter(([, vv]) => vv !== null && vv !== '' && vv !== undefined && typeof vv !== 'object');
    if (!entries.length) return <span className="text-muted-2">—</span>;
    const shown = entries.slice(0, INLINE_FIELD_CAP);
    return <>{shown.map(([kk, vv]) => `${humanize(kk)}: ${fmtP42Value(kk, vv)}`).join(' · ')}{entries.length > shown.length ? ` · +${entries.length - shown.length} more` : ''}</>;
  }
  return <>{fmtP42Value(k, v)}</>;
}

const TABLE_ROW_CAP = 15;
/** A compact, readable table for an array-of-records section — recurses into P42CellContent so a
 *  nested array-of-records renders as its own small table inline, never as a JSON blob. */
export function P42Table({ rows, rowCap = TABLE_ROW_CAP }: { rows: Record<string, unknown>[]; rowCap?: number }) {
  const cols = Array.from(rows.reduce((s, r) => { Object.keys(r).forEach((k) => s.add(k)); return s; }, new Set<string>()));
  const shown = rows.slice(0, rowCap);
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[11.5px] border-collapse">
        <thead><tr>{cols.map((c) => <th key={c} className="text-left text-muted font-semibold px-2 py-1 border-b border-line-2 whitespace-nowrap">{humanize(c)}</th>)}</tr></thead>
        <tbody>
          {shown.map((r, i) => (
            <tr key={i} className="odd:bg-canvas/50">
              {cols.map((c) => <td key={c} className="px-2 py-1 border-b border-line-2/60 align-top"><P42CellContent k={c} v={r[c]} /></td>)}
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length > shown.length && <div className="text-[11px] text-muted-2 mt-1">+{rows.length - shown.length} more row{rows.length - shown.length === 1 ? '' : 's'} — see raw JSON below.</div>}
    </div>
  );
}

/** Render one Probe42-sourced section: a table for arrays-of-records, a label/value list for an
 *  object (each value run through the same clean formatting/nesting as a table cell), or a single
 *  formatted value. Never raw JSON. `collapsed` wraps it in <details>. */
export function P42Section({ title, value, collapsed }: { title: string; value: unknown; collapsed?: boolean }) {
  if (value === undefined || value === null || (Array.isArray(value) && !value.length) || (typeof value === 'object' && !Array.isArray(value) && !Object.keys(value as object).length)) return null;
  const body = isArrayOfRecords(value) ? (
    <P42Table rows={value} />
  ) : Array.isArray(value) ? (
    <div className="text-[12px] text-navy">{value.map((x) => fmtP42Value(title, x)).join(', ')}</div>
  ) : typeof value === 'object' ? (
    <dl className="grid grid-cols-[minmax(140px,auto)_1fr] gap-x-4 gap-y-1.5 text-[12px]">
      {Object.entries(value as Record<string, unknown>).filter(([, v]) => v !== null && v !== '' && v !== undefined).map(([k, v]) => (
        <div key={k} className="contents"><dt className="text-muted">{humanize(k)}</dt><dd className="text-navy font-medium break-words"><P42CellContent k={k} v={v} /></dd></div>
      ))}
    </dl>
  ) : (
    <div className="text-[12px] text-navy">{fmtP42Value(title, value)}</div>
  );
  if (collapsed) {
    return (
      <details className="mb-2 border border-line-2 rounded-lg px-3 py-2">
        <summary className="text-[12px] font-semibold text-navy cursor-pointer">{title}</summary>
        <div className="mt-2">{body}</div>
      </details>
    );
  }
  return (
    <div className="mb-3">
      <div className="text-[12px] font-semibold text-navy mb-1.5">{title}</div>
      {body}
    </div>
  );
}

/** Small "via Probe42" credit — consistent styling everywhere a supplementary card cites the source. */
export const ViaRegistryIntel = () => <span className="text-[10px] font-semibold text-teal-d bg-teal-50 px-1.5 py-0.5 rounded">via Registry Intelligence</span>;

/**
 * The reusable "section summary" card — one per data-heavy section (Company snapshot, Litigation,
 * GST, Financials, Valuation, Related-party). Always shows a short, grounded plain-English summary;
 * a "↻ Refresh" button re-runs it bypassing the 24h cache; a collapsible "Show detailed analysis"
 * reveals the fuller breakdown (key insights / risks / positive signals / metric chips). Renders
 * nothing while there's genuinely no data for the section (not an empty/awkward card) — see
 * sectionInsight.ts's `hasAnyData` gate. Reused across every tab per decision.md D22 (one component,
 * not a bespoke summarizer per section) and the trailing "refreshable + collapsible" requirement.
 */
export function SectionInsight({ dealId, section, label }: { dealId: string; section: InsightSection; label?: string }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const queryKey = ['section-insight', dealId, section] as const;
  const { data, isFetching, isLoading, error } = useQuery({
    queryKey,
    queryFn: () => insightFetch(dealId, section),
    staleTime: 5 * 60_000,
  });
  const insight = data?.insight ?? null;

  async function refresh() {
    const res = await insightFetch(dealId, section, true);
    qc.setQueryData(queryKey, res);
  }

  if (isLoading) return <div className="mb-3 text-[11.5px] text-muted-2 animate-pulse">Summarizing…</div>;
  if (error) return null; // fail quiet — the detailed data below still renders fine without a summary
  if (!insight) return null; // nothing worth summarizing for this section on this deal

  const hasDetail = insight.keyInsights.length > 0 || insight.risks.length > 0 || insight.positives.length > 0 || Object.keys(insight.metrics).length > 0;

  return (
    <div className={`mb-3.5 pb-3.5 border-b border-line-2 rounded-lg ${insight.insufficientData ? '' : 'bg-teal-50/40 -mx-1 px-1 pt-1'}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          {label && <div className="text-[9.5px] font-bold uppercase tracking-[0.5px] text-muted-2 mb-1">{label}</div>}
          <div className="text-[12.5px] text-navy leading-snug">{insight.summary}</div>
        </div>
        <button
          onClick={refresh}
          disabled={isFetching}
          title="Refresh this summary"
          className="shrink-0 text-[11px] font-semibold text-teal-d hover:underline disabled:opacity-50 whitespace-nowrap"
        >
          {isFetching ? '…' : '↻ Refresh'}
        </button>
      </div>
      {hasDetail && (
        <div className="mt-1.5">
          <button onClick={() => setOpen((v) => !v)} className="text-[11px] font-semibold text-muted hover:text-navy">
            {open ? '▾ Hide detailed analysis' : '▸ Show detailed analysis'}
          </button>
          {open && (
            <div className="mt-2 grid gap-2.5 sm:grid-cols-2">
              {insight.keyInsights.length > 0 && (
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-[0.5px] text-muted-2 mb-1">Key insights</div>
                  <ul className="space-y-0.5">{insight.keyInsights.map((x, i) => <li key={i} className="text-[11.5px] text-navy">• {x}</li>)}</ul>
                </div>
              )}
              {insight.risks.length > 0 && (
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-[0.5px] text-red-700 mb-1">Risks / concerns</div>
                  <ul className="space-y-0.5">{insight.risks.map((x, i) => <li key={i} className="text-[11.5px] text-red-800">• {x}</li>)}</ul>
                </div>
              )}
              {insight.positives.length > 0 && (
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-[0.5px] text-teal-d mb-1">Positive signals</div>
                  <ul className="space-y-0.5">{insight.positives.map((x, i) => <li key={i} className="text-[11.5px] text-navy">• {x}</li>)}</ul>
                </div>
              )}
              {Object.keys(insight.metrics).length > 0 && (
                <div className="flex flex-wrap gap-1.5 content-start">
                  {Object.entries(insight.metrics).map(([k, v]) => (
                    <span key={k} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-navy-50 text-navy text-[11px] font-semibold">{k}: {v}</span>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
